import { Controller } from "@hotwired/stimulus"

/**
 * Sidebar Section Controller — Collapsible sidebar sections (Favorites, Apps, Teams).
 *
 * - Each section persists its open/closed state in localStorage
 * - Smooth height animation with opacity fade
 * - Chevron rotates on open/close
 * - Defaults to open on first visit
 * - Turbo-aware: skips restore if already in correct state
 */
export default class extends Controller {
  static targets = ["panel", "chevron"]
  static values = { key: String }

  connect() {
    this.storageKey = `90mod-section-${this.keyValue}`
    
    // Skip restore if this is a Turbo navigation and we're already initialized
    // (sidebar persists across navigations, no need to re-animate)
    if (this.element.dataset.initialized === "true") {
      return
    }
    
    this.restoreState()
    this.element.dataset.initialized = "true"
  }

  toggle(event) {
    event.preventDefault()
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
    this.updateAria()
    this.saveState()
  }

  open() {
    const panel = this.panelTarget
    this.element.classList.add("sidebar-section--open")

    // Measure content height
    panel.style.maxHeight = "none"
    panel.style.overflow = "visible"
    const height = panel.scrollHeight
    panel.style.maxHeight = "0px"
    panel.style.overflow = "hidden"

    // Force layout then animate
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.opacity = "1"
        panel.style.maxHeight = height + "px"
      })
    })

    const cleanup = () => {
      panel.style.maxHeight = "none"
      panel.style.overflow = "visible"
      panel.removeEventListener("transitionend", cleanup)
    }
    panel.addEventListener("transitionend", cleanup, { once: true })

    // Safety timeout
    setTimeout(() => {
      if (this.element.classList.contains("sidebar-section--open")) {
        panel.style.maxHeight = "none"
        panel.style.overflow = "visible"
      }
    }, 350)
  }

  close() {
    const panel = this.panelTarget
    this.element.classList.remove("sidebar-section--open")

    // Set explicit height so transition has a start value
    panel.style.overflow = "hidden"
    panel.style.maxHeight = panel.scrollHeight + "px"

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.opacity = "0"
        panel.style.maxHeight = "0px"
      })
    })
  }

  isOpen() {
    return this.element.classList.contains("sidebar-section--open")
  }

  saveState() {
    try {
      localStorage.setItem(this.storageKey, this.isOpen() ? "open" : "closed")
    } catch (_) { /* localStorage unavailable */ }
  }

  restoreState() {
    const panel = this.panelTarget

    // Disable transitions during restore to prevent flicker
    panel.style.transition = "none"

    let savedState = null
    try {
      savedState = localStorage.getItem(this.storageKey)
    } catch (_) { /* localStorage unavailable */ }

    // Default to open if no saved state
    const shouldOpen = savedState !== "closed"

    if (shouldOpen) {
      this.element.classList.add("sidebar-section--open")
      panel.style.opacity = "1"
      panel.style.maxHeight = "none"
      panel.style.overflow = "visible"
    } else {
      this.element.classList.remove("sidebar-section--open")
      panel.style.opacity = "0"
      panel.style.maxHeight = "0px"
      panel.style.overflow = "hidden"
    }

    this.updateAria()

    // Re-enable transitions after paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        panel.style.transition = ""
      })
    })
  }

  updateAria() {
    const trigger = this.element.querySelector('[data-action~="click->sidebar-section#toggle"]')
    if (trigger) trigger.setAttribute("aria-expanded", this.isOpen() ? "true" : "false")
  }
}
